import { useTranslation } from '@repo/i18n'
import { Laptop } from 'lucide-react'
import { type ReactNode, useSyncExternalStore } from 'react'

/**
 * Renders `children` everywhere except on a phone, where the whole app gives
 * way to one screen asking the visitor to switch to a computer (OPSSTD-9159).
 * Only the operator console sits behind it now; the client adapts to a phone's
 * width instead.
 *
 * A PHONE IS ITS USER AGENT, NOT ITS WIDTH. A narrow desktop window is still
 * a computer, and blocking it would lock out someone who only resized the
 * browser. `Mobi` is the token every phone browser carries and tablets do not
 * (MDN, "Browser detection using the user agent"), so tablets pass: the
 * desktop layout holds on their screens.
 *
 * THE iPAD IS THE ONE TABLET THAT SAYS `Mobile`, and only in some browsers.
 * Safari on iPadOS reports a Mac, but Chrome, Edge and Firefox there keep the
 * iOS shape, `(iPad; CPU OS …) … Mobile/15E148`. Without the exclusion the
 * same tablet would pass in one browser and be blocked in the next.
 *
 * The server cannot read `navigator`, so its snapshot is "not a phone": an
 * SSR profile renders the page and hydration swaps it for the block. The
 * default SPA profile renders in the browser only and never shows the page.
 *
 * The brand arrives as data because each app resolves its own branding; with
 * none published nothing branded is drawn and the screen still reads whole.
 */
function DesktopOnly({
	children,
	mark,
	productName
}: {
	children: ReactNode
	mark?: { light: string; dark: string }
	productName?: string
}) {
	const { t } = useTranslation()
	const isPhone = useSyncExternalStore(
		subscribeToNothing,
		() =>
			PHONE_USER_AGENT.test(navigator.userAgent) &&
			!IPAD_USER_AGENT.test(navigator.userAgent),
		() => false
	)

	if (!isPhone) {
		return children
	}

	return (
		<main className='flex min-h-full flex-col items-center justify-between gap-8 bg-background px-6 pt-16 pb-8 text-center text-foreground'>
			<div className='flex h-7 items-center gap-2.5'>
				{mark && (
					<>
						<img
							alt=''
							className='size-7 object-contain dark:hidden'
							height={28}
							src={mark.light}
							width={28}
						/>
						<img
							alt=''
							className='hidden size-7 object-contain dark:block'
							height={28}
							src={mark.dark}
							width={28}
						/>
					</>
				)}
				{productName && (
					<span className='font-semibold text-lg'>{productName}</span>
				)}
			</div>
			<div className='flex max-w-sm flex-col items-center gap-5'>
				<div className='flex size-18 items-center justify-center rounded-2xl bg-accent text-accent-foreground'>
					<Laptop aria-hidden='true' className='size-8' />
				</div>
				<h1 className='font-semibold text-2xl leading-tight'>
					{t('shared:desktop_only.title')}
				</h1>
				<p className='text-base text-muted-foreground leading-normal'>
					{t('shared:desktop_only.description')}
				</p>
			</div>
			<div aria-hidden='true' className='h-7' />
		</main>
	)
}

const PHONE_USER_AGENT = /Mobi/i
const IPAD_USER_AGENT = /iPad/

function subscribeToNothing() {
	return () => {
		// A device does not stop being a phone, so there is nothing to listen to.
	}
}

export { DesktopOnly }
